import { useCallback, useSyncExternalStore } from "react";
import type { ReadonlyRune, Rune } from "@rosette/ts/src/business/Rune.js";
import type { ReadonlyRuneWithEvents, RuneWithEvents } from "@rosette/ts/src/business/RuneWithEvents.js";


export function useOptionalRune<V extends Exclude<any, null | undefined>>(
    rune: ReadonlyRune<V> | ReadonlyRuneWithEvents<V, any> | Rune<V> | RuneWithEvents<V, any> | null | undefined,
): V | null {
    const subscribe = useCallback(
        (onChange: () => void) => {
            if (rune) {
                return rune.subscribe(onChange);
            } else {
                return () => {};
            }
        },
        [rune],
    );
    const get = useCallback(() => {
        return rune ? rune.get() : null;
    }, [rune]);
    return useSyncExternalStore(subscribe, get, get);
}


export function useRune<V extends Exclude<any, null | undefined>>(
    rune: ReadonlyRune<V> | ReadonlyRuneWithEvents<V, any> | Rune<V> | RuneWithEvents<V, any>,
): V {
    const subscribe = useCallback(
        (onChange: () => void) => rune.subscribe(onChange),
        [rune],
    );
    const get = useCallback(() => rune.get(), [rune]);
    return useSyncExternalStore(subscribe, get, get);
}


export function useDerivedRune<V extends Exclude<any, null | undefined>, R>(
    rune: ReadonlyRune<V> | ReadonlyRuneWithEvents<V, any> | Rune<V> | RuneWithEvents<V, any>,
    deriveFn: (value: V) => R,
): R {
    const get = useCallback(() => deriveFn(rune.get()), [rune, deriveFn]);
    const subscribe = useCallback(
        (onChange: () => void) => {
            let currentSnapshot = get();
            return rune.subscribe(() => {
                const newSnapshot = get();
                if (newSnapshot !== currentSnapshot) {
                    currentSnapshot = newSnapshot;
                    onChange();
                }
            });
        },
        [rune, get],
    );
    return useSyncExternalStore(subscribe, get, get);
}
